<!--
  Both patterns end in the same highlighted total row, which is rendered ONCE below rather than in
  each branch. The branches differ only in how the base amount is presented; the total is the same
  number arrived at the same way, and duplicating its markup is how the two patterns would drift.

  track $index, not track row.label: fee labels are resolved asynchronously by the host, so two rows
  could briefly share a label and a duplicate track key is a hard runtime error, not a glitch.
-->
<!--
  M4.22: the inline-control opt-out is a modifier on the CONTAINER rather than on the row, so the row
  class keeps meaning "this row projects a control" and nothing else. The alternative -- withholding
  --control from the rows that stay inline -- would make the class absent on a row that demonstrably
  contains a control, which is the kind of lie that costs an afternoon later.
-->
<div class="modal-price-breakdown"
  [class.modal-price-breakdown--inline-control]="inlineControlOnNarrow()"
  [id]="idPrefix() + '_breakdown'">

  @if (isDecomposed()) {
    <!-- Pattern A. The bold figure is the all-in price; the list beneath decomposes it. -->
    <div class="modal-price-breakdown__row modal-price-breakdown__row--headline">
      <span class="modal-price-breakdown__label" [id]="idPrefix() + '_base_label'">
        {{ translations().labelBaseAmount }}
      </span>
      <span class="modal-price-breakdown__amount" [id]="idPrefix() + '_all_in_amount'">
        {{ allInAmountDisplay() }}
      </span>
    </div>

    <!--
      A labelled list, so a screen reader announces these as components of the amount above rather
      than as peers of it. aria-label carries the host's translated string; the visual subordination
      is the bullet and the indent in the SCSS.

      M4.20: the <li> is a plain list item and the flex row is its CHILD. It used to be the row itself,
      which is why the bullets this comment has always described have never actually appeared on screen:
      a marker box is only generated for `display: list-item`, and &__row sets `display: flex`, which
      replaces it. The indent and the muting landed; the disc never did. One level of wrapping is the
      whole fix, and it is applied to both patterns so they cannot disagree about it again.
    -->
    <ul class="modal-price-breakdown__components" [attr.aria-label]="translations().ariaLabelPriceComponents">
      <li>
        <div class="modal-price-breakdown__row modal-price-breakdown__row--component">
          <span class="modal-price-breakdown__label">{{ translations().labelVehiclePrice }}</span>
          <span class="modal-price-breakdown__amount" [id]="idPrefix() + '_vehicle_price'">
            {{ baseAmountDisplay() }}
          </span>
        </div>
      </li>
      @for (row of feeRows(); track $index) {
        <li>
          <div class="modal-price-breakdown__row modal-price-breakdown__row--component">
            <span class="modal-price-breakdown__label">{{ row.label }}</span>
            <span class="modal-price-breakdown__amount" [id]="idPrefix() + '_component_fee_' + $index">
              {{ amountDisplay(row.amount) }}
            </span>
          </div>
        </li>
      }
    </ul>

    <!--
      Transportation stays OUTSIDE the list in this pattern, and that is the one place the two patterns
      deliberately differ (M4.20).

      The list above is labelled ariaLabelPriceComponents -- "what this price includes" -- and names its
      rows as components of the headline. allInAmount() computes that headline as base + REQUIRED fees
      with transportation passed as undefined, so transportation is provably not one of them: it is a
      charge added beneath the headline, which is why the total differs from it. Moving this row into the
      list to match pattern B would make the label state the opposite of the arithmetic to exactly the
      users who depend on the label.

      It keeps the bold additive weight for the same reason. See &__row--transportation in the SCSS.
    -->
    @if (showTransportationRow()) {
      <div class="modal-price-breakdown__row modal-price-breakdown__row--transportation">
        <span class="modal-price-breakdown__label">{{ translations().labelTransportationFee }}</span>
        <span class="modal-price-breakdown__amount" [id]="idPrefix() + '_transportation_fee'">
          {{ transportationDisplay() }}
        </span>
      </div>
    }
  } @else {
    <!--
      Pattern B. The base amount is bare -- typed by the buyer or offered by the seller.

      M4.21: --control marks the rows that project a stepper or text input instead of a figure, and it
      exists purely so the narrow-viewport rule in the SCSS can reach them and nothing else. It mirrors
      useProjectedAmount() rather than being derived from the slot, for the same reason that input
      exists at all -- an empty slot and no slot are indistinguishable from a content query.

      M4.22: it still mirrors useProjectedAmount() unconditionally. Whether that row actually stacks is
      decided by --inline-control on the container above, NOT by the presence of this class.
    -->
    <div class="modal-price-breakdown__row modal-price-breakdown__row--base"
      [class.modal-price-breakdown__row--control]="useProjectedAmount()">
      <span class="modal-price-breakdown__label" [id]="idPrefix() + '_base_label'">
        {{ translations().labelBaseAmount }}
      </span>
      @if (useProjectedAmount()) {
        <span class="modal-price-breakdown__control">
          <ng-content select="[appModalPriceBreakdownAmount]"></ng-content>
        </span>
      } @else {
        <span class="modal-price-breakdown__amount" [id]="idPrefix() + '_base_amount'">
          {{ baseAmountDisplay() }}
        </span>
      }
    </div>

    <!--
      A list for the same reason Pattern A's decomposition is one: these are repeated homogeneous
      rows, and a screen reader that announces "list, 2 items" tells the buyer how many charges are
      being added before reading them. Bare divs announce nothing, so the two patterns previously
      disagreed about the accessibility of the SAME fee rows from the SAME feeRows() input.

      Deliberately NOT labelled with ariaLabelPriceComponents. That string names the sub-rows as
      components OF the figure above them, which is true in Pattern A and false here: in Pattern B
      the row above is the buyer's own amount and these fees are added TO it, not contained in it.
      Borrowing the label to fill the attribute would tell a screen-reader user the opposite of the
      arithmetic. An unlabelled list still announces its size, which is the whole gain.

      role="list" is not decoration. &__fees sets list-style: none, which makes Safari drop the list
      role and silences the count this markup exists for; the attribute restores it. The two belong
      together -- see the comment on &__fees in the SCSS.

      This list is FLAT: no bullets, no indent, unlike Pattern A's decomposition. The indent is a claim
      of containment that is true there and false here, and the story owner ruled against borrowing it
      on 2026-10-02. Nothing in the markup encodes that either way; it is entirely &__fees vs
      &__components in the SCSS, which is why the two patterns can disagree about it safely.

      M4.20: the <li> wraps the row rather than being it -- same change and same reason as Pattern A
      above, and still required here even without a marker to suppress, because `display: flex` on the
      <li> also costs it its list-item semantics.

      Transportation is the LAST ITEM OF THIS LIST rather than a row below it. In this pattern it is a
      fee added to the buyer's amount in exactly the way the rows above it are, so setting it apart drew
      a distinction the arithmetic does not make -- a bold row below the list read as a subtotal of the
      list. Pattern A keeps it outside its list, where that distinction IS real; see the comment there.

      The list therefore renders when there is transportation but no fee rows at all, which is a real
      configuration: a listing with transportation quoted and buy-allInPricingRequiredFees empty.
    -->
    @if (feeRows().length || showTransportationRow()) {
      <ul class="modal-price-breakdown__fees" role="list">
        @for (row of feeRows(); track $index) {
          <li>
            <div class="modal-price-breakdown__row modal-price-breakdown__row--fee">
              <span class="modal-price-breakdown__label">{{ row.label }}</span>
              <span class="modal-price-breakdown__amount" [id]="idPrefix() + '_fee_' + $index">
                {{ amountDisplay(row.amount) }}
              </span>
            </div>
          </li>
        }
        <!--
          Same id and same class as Pattern A's copy, so every host hook, spec and the exported
          showsTransportationFee() contract are untouched by the move. One condition governs both
          copies: the hosting modal's "Transportation is optional" paragraph must read the same
          showTransportationRow() via a template ref, or the exported helper. Do not re-derive it.
        -->
        @if (showTransportationRow()) {
          <li>
            <div class="modal-price-breakdown__row modal-price-breakdown__row--transportation">
              <span class="modal-price-breakdown__label">{{ translations().labelTransportationFee }}</span>
              <span class="modal-price-breakdown__amount" [id]="idPrefix() + '_transportation_fee'">
                {{ transportationDisplay() }}
              </span>
            </div>
          </li>
        }
      </ul>
    }
  }

  <!--
    aria-live="polite" with aria-atomic: this figure changes as the buyer works the stepper or types,
    and it is the number they are bound to, so the label and the new amount are announced together
    rather than the amount alone.
  -->
  <div class="modal-price-breakdown__row modal-price-breakdown__row--total"
    aria-live="polite" aria-atomic="true">
    <span class="modal-price-breakdown__label">{{ translations().labelTotalDue }}</span>
    <span class="modal-price-breakdown__amount" [id]="idPrefix() + '_total_due'">
      {{ totalDueDisplay() }}
    </span>
  </div>

</div>
